import {
  CommunicationStudioClasses,
  CommunicationStudioCssVariables,
  CUSTOMISATION_CATEGORIES,
  SETTINGS_SCOPE,
} from "@tfg/types/constants/communication-studio.constant"
import { PRODUCT_ATTRIBUTES } from "@tfg/types/constants/configurations.constants"
import {
  ActivitiesComponentCustomisationConfiguration,
  FooterComponentCustomisationConfiguration,
  ContentFilteringProps,
} from "@tfg/types/schemas/communication-studio/configurationParameters"
import { ScopedConfigurationsResponse } from "@tfg/types/types/communication-studio/configurations.types"
import {
  OrganizationTitleCategoryStylingParameters,
  LogoCategoryStylingParameters,
  ProductAttributesCategoryStylingParameters,
  ProductImageCategoryStylingParameters,
  StylingParameters,
} from "@tfg/types/types/communication-studio/styling-parameters.types"

import { DEFAULT_ECOSCORE_SOURCE } from "./page-components/EcoScore"

export const DEFAULT_STYLING_PARAMETERS = {
  [CUSTOMISATION_CATEGORIES.GLOBAL]: {
    width: "100%",
    height: "100%",
    fontFamily: "Borna",
    letterSpacing: "0",
    lineHeight: "18px",
    backgroundColor: "#ffffff",
    headerFontFamily: {
      name: CommunicationStudioCssVariables.HEADER_FONT_FAMILY,
      value: "Borna",
      type: "cssVariable",
    },
    headerTextDecoration: {
      name: CommunicationStudioCssVariables.HEADER_TEXT_DECORATION,
      value: "none",
      type: "cssVariable",
    },
    headerTextTransform: {
      name: CommunicationStudioCssVariables.HEADER_TEXT_TRANSFORM,
      value: "none",
      type: "cssVariable",
    },
    headerLineHeight: {
      name: CommunicationStudioCssVariables.HEADER_LINE_HEIGHT,
      value: "18px",
      type: "cssVariable",
    },
    contentVerticalSpacing: {
      name: CommunicationStudioCssVariables.CONTENT_VERTICAL_SPACING,
      value: "1.5rem",
      type: "cssVariable",
    },
    headerFontWeight: {
      name: CommunicationStudioCssVariables.HEADER_FONT_WEIGHT,
      value: "700",
      type: "cssVariable",
    },
    textFontSize: {
      name: CommunicationStudioCssVariables.TEXT_FONT_SIZE,
      value: "14px",
      type: "cssVariable",
    },
    headerFontSize: {
      name: CommunicationStudioCssVariables.HEADER_FONT_SIZE,
      value: "20px",
      type: "cssVariable",
    },
    paddingBlock: {
      name: CommunicationStudioCssVariables.PADDING_BLOCK,
      value: "0.75rem",
      type: "cssVariable",
    },
    paddingInline: {
      name: CommunicationStudioCssVariables.PADDING_INLINE,
      value: "1.5rem",
      type: "cssVariable",
    },
    [CommunicationStudioClasses.APP_HEADER]: {
      name: CommunicationStudioClasses.APP_HEADER,
      type: "class",
      props: {
        backgroundColor: "#ffffff",
        marginBottom: "0px",
      },
    },
    textColor: {
      value: "#000000",
      type: "cssVariable",
      name: CommunicationStudioCssVariables.TEXT_COLOR,
    },
    [CommunicationStudioClasses.MAIN_CONTAINER]: {
      name: CommunicationStudioClasses.MAIN_CONTAINER,
      type: "class",
      props: {
        textAlign: "left",
        textTransform: "none",
      },
    },
    [CommunicationStudioClasses.CONTENT_BLOCKS]: {
      name: CommunicationStudioClasses.CONTENT_BLOCKS,
      type: "class",
      props: {
        gap: "1rem",
      },
    },
    titleColor: {
      value: "#000000",
      type: "cssVariable",
      name: CommunicationStudioCssVariables.TITLE_COLOR,
    },
    textFontWeight: {
      value: "400",
      type: "cssVariable",
      name: CommunicationStudioCssVariables.TEXT_FONT_WEIGHT,
    },
  },
  [CUSTOMISATION_CATEGORIES.ACTIVITIES]: {
    [CommunicationStudioClasses.ACTIVITY]: {
      name: CommunicationStudioClasses.ACTIVITY,
      type: "class",
      props: {
        textTransform: "uppercase",
        fontSize: "14px",
        fontWeight: "400",
        letterSpacing: "0",
      },
    },
    [CommunicationStudioClasses.COUNTRY]: {
      name: CommunicationStudioClasses.COUNTRY,
      type: "class",
      props: {
        textTransform: "none",
        fontSize: "14px",
        fontWeight: "400",
        letterSpacing: "0",
      },
    },
  },
  [CUSTOMISATION_CATEGORIES.PACKAGING_INFO]: {
    [CommunicationStudioClasses.PACKAGING_INFO_HYPER_LINK]: {
      name: CommunicationStudioClasses.PACKAGING_INFO_HYPER_LINK,
      type: "class",
      props: {
        fontWeight: "400",
        color: "#000000",
        textDecoration: "underline",
      },
    },
    [CommunicationStudioClasses.PACKAGING_INFO_HEADER]: {
      name: CommunicationStudioClasses.PACKAGING_INFO_HEADER,
      type: "class",
      props: {
        fontWeight: "700",
      },
    },
    [CommunicationStudioClasses.RECYCLED_PACKAGING_KEY_INFO]: {
      name: CommunicationStudioClasses.RECYCLED_PACKAGING_KEY_INFO,
      type: "class",
      props: {
        fontWeight: "700",
      },
    },
  },
  [CUSTOMISATION_CATEGORIES.SEARCH_ENGINE]: {
    backgroundColor: "#ffffff",
    searchEngineTextColor: {
      value: "#000000",
      type: "cssVariable",
      name: CommunicationStudioCssVariables.SEARCH_ENGINE_TEXT_COLOR,
    },
    productCardBadgeBackgroundColor: {
      value: "#000000",
      type: "cssVariable",
      name: CommunicationStudioCssVariables.PRODUCT_CARD_BADGE_BACKGROUND_COLOR,
    },
    productCardBadgeTextColor: {
      value: "#ffffff",
      type: "cssVariable",
      name: CommunicationStudioCssVariables.PRODUCT_CARD_BADGE_TEXT_COLOR,
    },
    fontFamily: "Borna",
    [CommunicationStudioClasses.SEARCH_ENGINE_HEADER]: {
      name: CommunicationStudioClasses.SEARCH_ENGINE_HEADER,
      type: "class",
      props: {
        backgroundColor: "#ffffff",
        color: "#000000",
      },
    },
  },
  [CUSTOMISATION_CATEGORIES.FOOTER]: {
    marginBottom: "0",
    marginTop: "0",
    [CommunicationStudioClasses.LAST_UPDATE]: {
      name: CommunicationStudioClasses.LAST_UPDATE,
      type: "class",
      props: {
        fontSize: "10px",
        fontWeight: "400",
      },
    },
  },
  [CUSTOMISATION_CATEGORIES.ECOSCORE]: {
    [CommunicationStudioCssVariables.ECOSCORE_ALIGN]: {
      name: CommunicationStudioCssVariables.ECOSCORE_ALIGN,
      type: "cssVariable",
      value: "center",
    },
  },
  [CUSTOMISATION_CATEGORIES.LOGO]: {
    [CommunicationStudioCssVariables.LOGO_ALIGN]: {
      name: CommunicationStudioCssVariables.LOGO_ALIGN,
      type: "cssVariable",
      value: "start",
    },
    [CommunicationStudioClasses.LOGO]: {
      name: CommunicationStudioClasses.LOGO,
      type: "class",
      props: {
        height: "100%",
        width: "48px",
      },
    },
  } satisfies LogoCategoryStylingParameters,
  [CUSTOMISATION_CATEGORIES.GENERIC_PASSPORT_TITLE]: {
    genericPassportTitle: {
      name: CommunicationStudioClasses.GENERIC_PASSPORT_TITLE,
      type: "class",
      props: {
        backgroundColor: "#ffffff",
        color: "#000000",
        fontSize: "20px",
        textTransform: "none",
        fontWeight: "700",
        textDecoration: "none",
        lineHeight: "18px",
        letterSpacing: "0",
        fontFamily: "Poppins",
        textAlign: "center",
      },
    },
  },
  [CUSTOMISATION_CATEGORIES.PRODUCT_ATTRIBUTES]: {
    [CommunicationStudioClasses.PRODUCT_ATTRIBUTES_PRIMARY_INFORMATION]: {
      name: CommunicationStudioClasses.PRODUCT_ATTRIBUTES_PRIMARY_INFORMATION,
      type: "class",
      props: {
        fontSize: "14px",
        fontWeight: "700",
        color: "#000000",
        textTransform: "none",
        textAlign: "left",
        fontFamily: "Borna",
      },
    },
    [CommunicationStudioClasses.PRODUCT_ATTRIBUTES_SECONDARY_INFORMATION]: {
      name: CommunicationStudioClasses.PRODUCT_ATTRIBUTES_SECONDARY_INFORMATION,
      type: "class",
      props: {
        fontSize: "14px",
        fontWeight: "700",
        color: "#000000",
        textTransform: "none",
        textAlign: "left",
        fontFamily: "Borna",
      },
    },
    [CommunicationStudioClasses.PRODUCT_ATTRIBUTES_CONTAINER]: {
      name: CommunicationStudioClasses.PRODUCT_ATTRIBUTES_CONTAINER,
      type: "class",
      props: {
        gap: "1rem",
        marginBottom: "0.015rem",
        alignItems: "start",
      },
    },
  } satisfies ProductAttributesCategoryStylingParameters,
  [CUSTOMISATION_CATEGORIES.ORGANIZATION_TITLE]: {
    [CommunicationStudioClasses.ORGANIZATION_TITLE]: {
      name: CommunicationStudioClasses.ORGANIZATION_TITLE,
      type: "class",
      props: {
        fontSize: "14px",
        fontWeight: "700",
        color: "#000000",
        textTransform: "none",
        fontFamily: "Borna",
        textAlign: "left",
        backgroundColor: "#ffffff",
        lineHeight: "normal",
      },
    },
  } satisfies OrganizationTitleCategoryStylingParameters,
  [CUSTOMISATION_CATEGORIES.PRODUCT_IMAGE]: {
    [CommunicationStudioClasses.PRODUCT_IMAGE]: {
      name: CommunicationStudioClasses.PRODUCT_IMAGE,
      type: "class",
      props: {
        width: "100%",
        height: "100%",
      },
    },
    [CommunicationStudioCssVariables.IMAGE_ALIGN]: {
      name: CommunicationStudioCssVariables.IMAGE_ALIGN,
      type: "cssVariable",
      value: "center",
    },
  } satisfies ProductImageCategoryStylingParameters,
} satisfies StylingParameters

export const DEFAULT_CONFIGURATIONS = {
  [SETTINGS_SCOPE.COMPONENT_CUSTOMISATION]: {
    [CUSTOMISATION_CATEGORIES.ACTIVITIES]: {
      showActivityIcons: true,
      showCountryFlags: true,
      showCertificates: true,
      activitiesDepth: 3,
    } satisfies ActivitiesComponentCustomisationConfiguration,
    [CUSTOMISATION_CATEGORIES.FOOTER]: {
      showLastUpdateTimestamp: true,
      showPoweredByTFG: true,
    } satisfies FooterComponentCustomisationConfiguration,
  },
  [SETTINGS_SCOPE.CONTENT_FILTERING]: {
    showTraceabilityDetails: true,
    standardsToBeShown: [],
    logoFile: null,
    primaryProductAttributes: [
      PRODUCT_ATTRIBUTES.NAME,
      PRODUCT_ATTRIBUTES.BRAND_REFERENCE,
    ],
    secondaryProductAttributes: [PRODUCT_ATTRIBUTES.COLOR],
    ecoScoreDataSource: DEFAULT_ECOSCORE_SOURCE,
  } satisfies ContentFilteringProps,
} satisfies ScopedConfigurationsResponse
